import ModalLayout from "@/ui/modals/ModalLayout.tsx";
import st from "@/styles/Ui.module.scss";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import CommonClass from "@/utils/common.ts";
import {useEffect, useRef, useState} from "react";
import LoginBack from "@/components/LoginBack.tsx";
import useAuth from "@/hooks/auth.ts";
import {useTranslation} from "react-i18next";
import Button from "@/ui/Button.tsx";
import {useAtom} from "jotai/index";
import {all_inits, login_back_interval, pin_value} from "@/atoms/common.ts";
import SessionExpiresWarn from "@/ui/SessionExpiresWarn.tsx";
import LoginLoader from "@/ui/LoginLoader.tsx";
import {auth_req} from "@/services/account.ts";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {country_code} from "@/utils/constants.ts";
import WritePinFields from "@/ui/WritePinFields.tsx";
// import {device_uid} from "@/atoms/profile.ts";
import ResendCounter from "@/ui/ResendCounter.tsx";
import useIntervals from "@/hooks/intervals.ts";
import useCalc from "@/hooks/calculator.ts";
import useLoans from "@/hooks/loans.ts";
import useNotifications from "@/hooks/notifications.ts";



const LoginBackModal = () => {
    const [, set_init] = useAtom(all_inits)
    const [open, set_open] = useState<boolean>(false)
    const auth = useAuth()
    const user = auth.current_user()
    const {t} = useTranslation()
    const [_reqLoad, setReqLoad] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)
    const [_error, setError] = useState(false)
    const {executeRecaptcha} = useGoogleReCaptcha();
    const [show_pin, set_show_pin] = useState(false)
    const [pin_fields] = useAtom(pin_value);
    const intervals_hook = useIntervals(login_back_interval)
    const queryParams = new URLSearchParams(location.search);
    const debug = queryParams.get("debug")
    const calc = useCalc()
    const loans = useLoans()
    const notif = useNotifications()
    const timeout_ref = useRef<any>(null)
    const effect_ran = useRef<boolean>(false)

    const counter_enabled = useRef<boolean>(true)

    const trigger_counter = async () => {
        if (!counter_enabled.current) return

        await notif.update_count()

        if (!counter_enabled.current) return

        timeout_ref.current = setTimeout(trigger_counter, 5000)
    }

    const handleSetReqLoad = (event:any) => {
        const { value } = event.detail;
        setReqLoad(value);
    };

    const open_handler = (x:boolean) => {
        if (x) {
            counter_enabled.current = false
            clearTimeout(timeout_ref.current)
        } else {
            counter_enabled.current = true
            clearTimeout(timeout_ref.current)
            trigger_counter()
        }
        set_open(x)
    }

    const email1 = async (email:string) => {
        let token: string | undefined = undefined

        if (!CommonClass.isEmailValid(email)) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"))
            setError(true)
            return
        }

        if (CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                return;
            }
            const newToken = await executeRecaptcha('auth');
            token = newToken;
        }

        setLoading(true)
        const req = await auth_req().email_st_1(email, token)
        setError(false)

        if(req?.status === 200) {
            intervals_hook.setIntervalHandler()
            setLoading(false)
            set_show_pin(true)
            eventEmitter.emit(CommonClass.CustomEvents.set_resend_interval, false)
            return
        }

        if (req?.status === 429 || req?.status === 403) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("att_limit"))

            setLoading(false)
            return
        }

        if (req?.status === 500) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            setLoading(false)
            return
        }

        if (req?.error) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_email_err"))

            setError(true)
            setLoading(false)
            return
        }

    }

    const phone_1 = async (phone:string) => {
        let token: string | undefined = undefined

        if(CommonClass.general_settings.recaptcha.is_enabled) {
            token = await auth.getRecaptchaToken()
        }

        setLoading(true)
        const req = await auth_req().phone_st_1(phone.replace(country_code, ""), token)
        setError(false)

        if(req?.status === 200) {
            setLoading(false)
            set_show_pin(true)
            intervals_hook.setIntervalHandler()
            eventEmitter.emit(CommonClass.CustomEvents.set_resend_interval, false)
            return
        }

        if (req?.status === 429 || req?.status === 403) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("att_limit"))

            setLoading(false)
            return
        }

        if (req?.status === 400) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("valid_nr_err"))

            setLoading(false)
            setError(true)
            return
        }

        if (req?.status === 500) {
            eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))

            setLoading(false)
            return
        }

    }

    const step1 = async () => {

        if(user?.auth_type === "ByPhone"){
            phone_1(user?.phone)
        }

        if(user?.auth_type === "ByEmail"){
            email1(user?.email)
        }
    }

    const step2 = async (type:string) => {
        let token:string | undefined = undefined
        let val = type === "email" ? user?.email : user?.phone.replace(country_code, "")
        if(CommonClass.general_settings.recaptcha.is_enabled) {
            if (!executeRecaptcha) {
                return;
            }
            const newToken = await executeRecaptcha('homepage');
            token = newToken;
        }

        let pwd = pin_fields.join("")


        const req = type === "email" ?
            await auth_req().email_st_2(pwd, val || "", token) :
            await auth_req().phone_st_2(pwd, val || "", token)

        if(req?.status === 429) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar,"error", t("att_limit"))
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("att_limit"))
            setReqLoad(false)
            return
        }

        if(req?.status === 400) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)

            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            setError(true)
            setReqLoad(false)
            return
        }

        if(req?.status === 500) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)

            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            setError(false)
            setReqLoad(false)
            return
        }

        if(req?.status === 200 && typeof req.response === "object" && req.response?.message === "ok" && req.response?.session_token) {
            CommonClass.init_auth = true
            eventEmitter.emit(CommonClass.CustomEvents.reset_states)

            localStorage.setItem(CommonClass.local_storage_fields.session_token, req.response.session_token)
            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text,"")
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar,"success", t("auth_success"))

            let timeout = setTimeout(async () => {
                if(debug === "true") console.info("setInterval StepTwoForm")

                if(auth.isAuth()){
                    auth.small_update()
                    eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
                    eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)
                }
                await calc.update_calculator_limits()
                await loans.updPendingLoans()
                setReqLoad(false)

                clearTimeout(timeout)
            }, 500)
            // eventEmitter.emit(CommonClass.CustomEvents.reset_states)
            set_show_pin(false)
        }

        if(req?.status === 403) {
            eventEmitter.emit(CommonClass.CustomEvents.reset_otp_value)
            eventEmitter.emit(CommonClass.CustomEvents.reset_pwd_value)

            eventEmitter.emit(CommonClass.CustomEvents.set_pwd_err_text, t("error_message"))
            // eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "error", t("error_message"))
            setError(true)
            setReqLoad(false)
            return
        }
    }


    useEffect(() => {
        if(!effect_ran.current) {
            trigger_counter()
        }

        window.addEventListener(CommonClass.CustomEvents.set_req_load, handleSetReqLoad)
        eventEmitter.on(CommonClass.CustomEvents.session_expired, open_handler)

        return () => {
            counter_enabled.current = false
            clearTimeout(timeout_ref.current)
            window.removeEventListener(CommonClass.CustomEvents.set_req_load, handleSetReqLoad)
            eventEmitter.off(CommonClass.CustomEvents.session_expired, open_handler)
            effect_ran.current = true
        }

    }, []);

    useEffect(() => {
        if(pin_fields.length === 4) {
            let completed = true

            pin_fields.map((x:any) => {
                if(x === "" || x === " ") {
                    completed = false
                }

            })
            if(completed && open) {
                if(user?.auth_type === "ByEmail") {
                    step2("email")
                } else if (user?.auth_type === "ByPhone") {
                    step2("phone")
                }
            }
        }
    }, [pin_fields, open]);

    return (
        <ModalLayout
            className={st.login_back_modal}
            open={open}
            // open={true}
            innerClassName={st.login_back_modal_inner}
            removeClose
            id={"login_back"}
        >

            <>
                {(user && user.is_pass_code_set) ? (
                    <LoginBack
                        style={st}
                        token_expired
                    />
                ) : (
                    <>

                        {user?.auth_type === "ByMPass" ? (
                            <div className={st.send_otp}>
                                {!show_pin && (
                                    <SessionExpiresWarn/>
                                )}

                                <div className={st.controllers}>

                                    <Button
                                        onClick={() => {
                                            auth.authMpass("individual")
                                        }}
                                        type={"button"}
                                        className={st.send_otp}
                                    >
                                        <span>
                                            {t("logging_back")}
                                        </span>

                                    </Button>

                                </div>
                            </div>
                        ) : (
                            <div className={st.send_otp}>
                                {!show_pin && (
                                    <SessionExpiresWarn/>
                                )}

                                {(() => {

                                    if (typeof user !== undefined) {

                                        if (user?.auth_type === "ByPhone") {
                                            return (
                                                <span className={st.inf}>
                                                    {show_pin ? t("code_sent_to_nr") : t("send_otp_for_number")} {user.phone}
                                                </span>
                                            )

                                        } else if (user?.auth_type === "ByEmail") {

                                            return (
                                                <span className={st.inf}>
                                                    {show_pin ? t("code_sent_to_email") : t("send_otp_for_email")} {user.email}
                                                </span>
                                            )

                                        }
                                    }
                                })()}

                                {show_pin && (
                                    <WritePinFields error={_error} className={st.pin}/>
                                )}

                                <div className={st.controllers}>

                                    {!show_pin && (
                                        <Button
                                            onClick={step1}
                                            type={"button"}
                                            className={st.send_otp}
                                        >
                                            <span>
                                                {t("send")}
                                            </span>

                                        </Button>
                                    )}

                                    {show_pin && (
                                        <ResendCounter
                                            className={st.resend_otp}
                                            onClick={() => {
                                                step1()
                                            }}
                                            interval_atom={login_back_interval}
                                        />
                                    )}

                                    <button
                                        onClick={() => {
                                            set_init(false)
                                            let timeout = setTimeout(() => {
                                                auth.logOut()
                                                clearTimeout(timeout)
                                            }, 500)
                                        }}
                                        type={"button"}
                                        className={st.exit}
                                    >
                                        <span>
                                            {t("exit")}
                                        </span>

                                    </button>

                                </div>

                                {loading &&
                                    <LoginLoader/>
                                }
                            </div>

                        )}

                    </>
                )}
            </>

        </ModalLayout>
    )
}

export default LoginBackModal
